'use client';

import { useEffect, useRef } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import type { ModalProps } from '../modal';
import { openSaveBeforeLeavePrompt } from './open-save-before-leave-prompt';
import { useConfirmModal } from './use-confirm-modal';

interface UseWarnBeforeLeaveParams {
  /** When false the guard is inert — no prompts on any exit path. */
  shouldWarn: boolean;
  /** Branded confirmation title (in-app navigation only). */
  title: string;
  /** Branded confirmation body copy (in-app navigation only). */
  message: string;
  /** Label for the secondary "leave anyway" action. */
  leaveLabel: string;
  /** Label for the "stay on the page" action: the primary one, unless there's a `save`. */
  stayLabel: string;
  variant?: ModalProps['variant'];
  /**
   * Adds a primary action, `label`, that leaves once `onSave` resolves `true` and stays when it
   * resolves `false`. `onSave` reports its own failure.
   */
  save?: { label: string; onSave: () => Promise<boolean> };
}

type AppRouterInstance = ReturnType<typeof useRouter>;
type RouterHref = Parameters<AppRouterInstance['push']>[0];
type RouterNavOptions = Parameters<AppRouterInstance['push']>[1];

/**
 * Resolve a `router.push` / `replace` href to pathname+search when possible so
 * same-route no-ops can skip the leave prompt.
 */
const resolveHrefPath = (href: RouterHref): string | null => {
  if (typeof href !== 'string') {
    return null;
  }

  try {
    const url = new URL(href, window.location.href);
    if (url.origin !== window.location.origin) {
      return null;
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
};

/**
 * App Router leave guard for unsaved in-memory work. Covers:
 *
 * - Browser exit (reload / tab close): native `beforeunload`
 * - In-app link clicks (TopNav, `<Link>`): capture-phase interception + branded modal
 * - Programmatic `router.push` / `router.replace` on this hook's own router: temporary method wrap +
 *   branded modal. `useRouter()` hands each component its own object, so a push from another
 *   component (e.g. TopNav's New Project button) is not guarded.
 * - Browser Back/Forward: `popstate` held off ahead of Next's router, URL restored + branded modal
 *
 * Confirmed leave sets `hasConfirmedLeave` for the brief window before unmount so
 * a second exit path does not re-prompt. We intentionally do **not** re-arm if the
 * navigation fails and the page stays mounted: App Router leaves call the unwrapped
 * `push`/`replace` after confirm (so the same leave is not re-intercepted), and a
 * failed soft nav that leaves this page mounted is rare enough that a timeout-based
 * re-arm is not worth the race of resetting before a successful navigation finishes.
 */
export const useWarnBeforeLeave = ({
  shouldWarn,
  title,
  message,
  leaveLabel,
  stayLabel,
  variant,
  save,
}: UseWarnBeforeLeaveParams) => {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { confirm } = useConfirmModal({
    title,
    confirmText: stayLabel,
    cancelText: leaveLabel,
    variant,
  });

  const shouldWarnRef = useRef(shouldWarn);
  shouldWarnRef.current = shouldWarn;

  const openPrompt = (leave: () => void, onClose: () => void) => {
    if (save) {
      openSaveBeforeLeavePrompt({
        title,
        message,
        leaveLabel,
        stayLabel,
        saveLabel: save.label,
        variant,
        onLeave: leave,
        onSave: async () => {
          if (await save.onSave()) leave();
        },
        onClose,
      });
      return;
    }
    confirm({
      confirmationMessage: message,
      // Staying is the primary action; leaving, which discards the work, is the secondary one.
      onConfirm: () => {},
      onCancel: leave,
      onClose,
    });
  };
  const openPromptRef = useRef(openPrompt);
  openPromptRef.current = openPrompt;

  useEffect(() => {
    if (!shouldWarn) {
      return undefined;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [shouldWarn]);

  useEffect(() => {
    let hasConfirmedLeave = false;
    let isPromptOpen = false;
    // The history entry this page stands on, to put back when a Back/Forward is held off.
    const currentEntry = {
      state: window.history.state as unknown,
      path: `${window.location.pathname}${window.location.search}`,
    };

    const openLeavePrompt = (proceed: () => void) => {
      if (isPromptOpen) {
        return;
      }
      isPromptOpen = true;
      openPromptRef.current(
        () => {
          hasConfirmedLeave = true;
          proceed();
        },
        () => {
          isPromptOpen = false;
        },
      );
    };

    const guardNavigation = (
      original: AppRouterInstance['push'],
      href: RouterHref,
      options?: RouterNavOptions,
    ) => {
      if (!shouldWarnRef.current || hasConfirmedLeave) {
        return original(href, options);
      }

      const nextPath = resolveHrefPath(href);
      if (nextPath != null) {
        const currentPath = `${window.location.pathname}${window.location.search}`;
        if (nextPath === currentPath) {
          return original(href, options);
        }
      }

      openLeavePrompt(() => {
        original(href, options);
      });
    };

    const mutableRouter = router as AppRouterInstance;
    const originalPush = mutableRouter.push.bind(mutableRouter);
    const originalReplace = mutableRouter.replace.bind(mutableRouter);
    mutableRouter.push = ((href, options) =>
      guardNavigation(originalPush, href, options)) as AppRouterInstance['push'];
    mutableRouter.replace = ((href, options) =>
      guardNavigation(originalReplace, href, options)) as AppRouterInstance['replace'];

    const handleDocumentClick = (event: MouseEvent) => {
      if (!shouldWarnRef.current || hasConfirmedLeave) {
        return;
      }
      if (event.defaultPrevented) {
        return;
      }
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const anchor = target.closest('a');
      if (!anchor || !(anchor instanceof HTMLAnchorElement)) {
        return;
      }
      if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
        return;
      }

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      let nextUrl: URL;
      try {
        nextUrl = new URL(href, window.location.href);
      } catch {
        return;
      }

      if (nextUrl.origin !== window.location.origin) {
        return;
      }

      const nextPath = `${nextUrl.pathname}${nextUrl.search}`;
      const currentPath = `${window.location.pathname}${window.location.search}`;
      if (nextPath === currentPath) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      openLeavePrompt(() => {
        originalPush(nextPath);
      });
    };

    // Next's own popstate listener navigates, and React commits that before a later listener
    // runs — unmounting this page and its listener first. So this one listens in the capture phase,
    // ahead of Next, and stops the event from reaching it.
    const handlePopState = (event: PopStateEvent) => {
      const intendedPath = `${window.location.pathname}${window.location.search}`;
      if (!shouldWarnRef.current || hasConfirmedLeave || intendedPath === currentEntry.path) {
        return;
      }
      event.stopImmediatePropagation();

      // History already stepped; put this page's entry back and re-navigate on confirm.
      window.history.pushState(currentEntry.state, '', currentEntry.path);
      openLeavePrompt(() => {
        originalPush(intendedPath);
      });
    };

    document.addEventListener('click', handleDocumentClick, true);
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      mutableRouter.push = originalPush;
      mutableRouter.replace = originalReplace;
      document.removeEventListener('click', handleDocumentClick, true);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [router, pathname, searchParams]);
};
